import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import doFetch from '../../../api/doFetch';

const context = createContext();

export const defaultValue = { period: 'day', dealers: [], channel: null, status: null, vin: null };

export default function Provider({ children }) {
  const history = useHistory();

  const [filter, setFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlPeriod = params.get('period');
    const urlDealers = params.getAll('dealers');
    const urlChannel = params.get('channel');
    const urlStatus = params.get('status');
    const urlVin = params.get('vin');

    return {
      period: ['day', 'week', 'month'].includes(urlPeriod) ? urlPeriod : defaultValue.period,
      dealers:
        urlDealers.length > 0
          ? urlDealers.map((id) => ({ hmsId: Number(id) }))
          : defaultValue.dealers,
      channel: ['api', 'api_ai', 'mcp'].includes(urlChannel) ? urlChannel : defaultValue.channel,
      status: ['verified', 'not_found'].includes(urlStatus) ? urlStatus : defaultValue.status,
      vin: urlVin || defaultValue.vin
    };
  });

  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const reloadData = async () => {
    setIsLoading(true);
    const params = new URLSearchParams();
    params.set(`period`, filter.period);
    if (filter.dealers?.length) {
      filter.dealers.forEach((d) => params.append('hmsIds', d.hmsId));
    }
    if (filter.channel?.value) params.set('channel', filter.channel?.value);
    if (filter.status?.value) params.set('status', filter.status?.value);
    if (filter.vin) params.set('vin', filter.vin);

    const data = await doFetch(`/lotlenz/partner-logs/stats?${params.toString()}`);
    if (data) {
      setStats({
        ...data,
        byPartner: (data.byPartner || []).map((p) => ({
          ...p,
          verifiedRate: p.total ? Math.round((100 / p.total) * (p.verified || 0)) : 0,
          notFoundRate: p.total ? Math.round((100 / p.total) * (p.notFound || 0)) : 0
        }))
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    reloadData();
    // eslint-disable-next-line
  }, [filter]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('period', filter.period);
    if (filter.dealers?.length) {
      filter.dealers.forEach((d) => params.append('dealers', d.hmsId));
    }
    if (filter.channel?.value) params.set('channel', filter.channel?.value);
    if (filter.status?.value) params.set('status', filter.status?.value);
    if (filter.vin) params.set('vin', filter.vin);

    history.replace({ search: `?${params.toString()}` });
    // eslint-disable-next-line
  }, [filter]);

  const summary = useMemo(() => {
    if (!stats) return null;
    const { current, previous } = stats;
    return {
      ...current,
      verifiedRate: current.total ? Math.round((100 / current.total) * (current.verified || 0)) : 0,
      totalTrend: current.total - previous.total,
      verifiedTrend: current.verified - previous.verified,
      notFoundTrend: current.notFound - previous.notFound,
      avgRespTrend: previous.avgResponseTimeMs - current.avgResponseTimeMs
    };
  }, [stats]);

  const contextValue = useMemo(
    () => ({ filter, setFilter, isLoading, stats, summary }),
    [filter, isLoading, stats, summary]
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
}

export const useData = () => {
  const { stats, isLoading, summary } = useContext(context);
  return { stats, isLoading, summary };
};

export const useFilter = () => useContext(context).filter;

export const usePatchFilter = () => {
  const { setFilter } = useContext(context);
  return (patch) => setFilter((f) => ({ ...f, ...patch }));
};

export const useResetFilter = () => {
  const { setFilter } = useContext(context);

  return async () => {
    setFilter(defaultValue);
  };
};
